import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Lock, EnvelopeSimple, ShieldCheck, CheckCircle } from "@phosphor-icons/react";
import { resetGreeting } from "@/components/diwali/greetSession";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(() => {
    const expired = new URLSearchParams(window.location.search).get("expired") || sessionStorage.getItem("session_expired");
    sessionStorage.removeItem("session_expired");
    return expired ? "Your session has expired. Please sign in again." : "";
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { await login(email, password); resetGreeting(); nav("/dashboard"); }
    catch (err) { setError(formatError(err)); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 login-aurora border-r border-border">
        <div className="flex items-center gap-3">
          <img src="/logibyte-logo.png" alt="Logibyte" className="h-12 w-12 rounded-xl object-contain" />
          <div>
            <div className="text-base font-bold tracking-tight text-[#0F172A] font-head">LOGIBYTE TECHNOLOGIES</div>
            <div className="text-xs text-muted-foreground">Maidvy Operations CRM</div>
          </div>
        </div>
        <div>
          <h2 className="text-4xl font-bold tracking-tight leading-tight max-w-md text-[#0F172A]">
            The command center for Maidvy operations.
          </h2>
          <p className="text-muted-foreground mt-4 max-w-sm">
            Leads, bookings, allocation, payments and the maid database — one clean, secure workspace for your team.
          </p>
          <ul className="mt-8 space-y-3 max-w-sm">
            {["Real-time lead & booking pipeline", "Maid allocation & deployment tracking", "Complete audit trail on every action"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-sm text-foreground/80">
                <CheckCircle size={18} weight="fill" className="text-gold" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <p className="text-[11px] text-muted-foreground uppercase tracking-widest">Powered by Logibyte Technologies</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src="/logibyte-logo.png" alt="Logibyte" className="h-10 w-10 rounded-lg object-contain" />
            <div>
              <div className="text-sm font-bold tracking-tight text-[#0F172A] font-head">LOGIBYTE TECHNOLOGIES</div>
              <div className="text-[11px] text-muted-foreground">Maidvy Operations CRM</div>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 text-xs brand-gold border border-primary/30 bg-accent rounded-full px-3 py-1 mb-6">
            <ShieldCheck size={14} /> Secure Employee Access
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">Welcome back</h1>
          <p className="text-muted-foreground text-sm mb-8">Sign in with your official Maidvy email.</p>

          {error && <div data-testid="login-error" className="mb-4 text-sm text-destructive border border-destructive/30 bg-destructive/10 rounded-lg px-3 py-2">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
              <div className="relative mt-1.5">
                <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@maidvy.in" className="pl-9 bg-secondary border-transparent h-11 focus-visible:ring-primary focus-visible:bg-white" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
              <div className="relative mt-1.5">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none" />
                <PasswordInput data-testid="login-password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="pl-9 bg-secondary border-transparent h-11 focus-visible:ring-primary focus-visible:bg-white" />
              </div>
            </div>
          </div>

          <Button data-testid="login-submit" type="submit" disabled={loading}
            className="w-full mt-6 h-11 bg-gold text-white hover:brightness-95 font-semibold rounded-lg shadow-sm">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-[11px] text-muted-foreground mt-6 text-center">Protected system. All activity is logged and audited.</p>
        </form>
      </div>
    </div>
  );
}
